import React, { useEffect, useMemo, useRef, useState } from 'react';
import LoadingScreen from '../components/LoadingScreen';
import Header from '../components/Header';
import Link from '../components/Link';
import PageTitle from '../components/PageTitle';
import styles from './styles/Distribuicoes.module.css';

const TABELAS = {
  3: '3 - S¹',
  6: '6 - Lojas Antigo',
  7: '7 - Consultoras Antigo',
  10: '10 - Atacado',
  12: '12 - S²'
};
const FABRICAS = ['10', '20', '30'];

function safeNum(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function formatNum(v) {
  return safeNum(v).toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

function formatBRL(v) {
  return safeNum(v).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function isoDate(year, month, day) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

function firstDayOfYear(d = new Date()) {
  const y = d.getFullYear();
  return isoDate(y, 1, 1);
}

function lastDayOfYear(d = new Date()) {
  const y = d.getFullYear();
  return isoDate(y, 12, 31);
}

const Distribuicoes = () => {
  const hoje = useMemo(() => new Date(), []);
  const [filtros, setFiltros] = useState({
    dataIni: firstDayOfYear(hoje),
    dataFim: lastDayOfYear(hoje),
    codItem: '',
    tabela: 10,
  });

  const [grupos, setGrupos] = useState([]); // lista vinda do backend
  const [gruposSelecionados, setGruposSelecionados] = useState(new Set());
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [erro, setErro] = useState('');
  const [payload, setPayload] = useState(null);

  const dropdownRef = useRef(null);

  // fecha dropdown ao clicar fora
  useEffect(() => {
    const onDocClick = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // carrega grupos (distinct descr_grupo)
  useEffect(() => {
    (async () => {
      try {
        const resp = await fetch('https://provin.ind.br/api/distribuicoes/grupos');
        const data = await resp.json();
        if (!resp.ok) throw new Error(data?.error || 'Erro ao buscar grupos.');
        setGrupos(Array.isArray(data) ? data : []);
      } catch (e) {
        // não trava a página se falhar, só loga
        console.error(e);
        setGrupos([]);
      }
    })();
  }, []);

  const fetchDados = async (override = null) => {
    const f = override || filtros;
    setIsLoading(true);
    setErro('');

    try {
      const params = new URLSearchParams();
      params.set('dataIni', f.dataIni);
      params.set('dataFim', f.dataFim);
      params.set('tabela', String(f.tabela));
      if (f.codItem) params.set('codItem', f.codItem);

      // grupos (repeated query params)
      Array.from(gruposSelecionados).forEach((g) => {
        params.append('grupos', g);
      });

      const resp = await fetch(`https://provin.ind.br/api/distribuicoes/fluxo?${params.toString()}`, {
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await resp.json();
      if (!resp.ok) throw new Error(data?.error || 'Erro ao buscar dados.');

      setPayload(data);
    } catch (e) {
      setErro(e.message || 'Erro inesperado.');
      setPayload(null);
    } finally {
      setIsLoading(false);
    }
  };

  // load inicial
  useEffect(() => {
    fetchDados();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dados = payload?.dados;
  const refs = payload?.referencias;

  const enviosTotais = useMemo(() => {
    const qtde = safeNum(dados?.envios_totais?.qtde);
    const valor = safeNum(dados?.envios_totais?.valor);
    return { qtde, valor };
  }, [dados]);

  const enviosPorLoja = useMemo(() => {
    const arr = Array.isArray(dados?.envios_por_loja) ? dados.envios_por_loja : [];
    return arr.map((x) => ({
      loja: String(x.loja || 'OUTROS'),
      qtde: safeNum(x.qtde),
      valor: safeNum(x.valor),
    }));
  }, [dados]);

  const devolPorLoja = useMemo(() => {
    const arr = Array.isArray(dados?.devolucoes_por_loja) ? dados.devolucoes_por_loja : [];
    return arr.map((x) => ({
      loja: String(x.loja || 'OUTROS'),
      qtde: safeNum(x.qtde),
      valor: safeNum(x.valor),
    }));
  }, [dados]);

  const enviosLojaFabrica = useMemo(() => {
    const arr = Array.isArray(dados?.envios_por_loja_e_fabrica) ? dados.envios_por_loja_e_fabrica : [];
    return arr.map((x) => ({
      loja: String(x.loja || 'OUTROS'),
      empresa: String(x.empresa || ''),
      qtde: safeNum(x.qtde),
      valor: safeNum(x.valor),
    }));
  }, [dados]);

  // Mapa loja -> { empresaCode, clienteCode, nome }
  const lojaMetaMap = useMemo(() => {
    const map = new Map();
    const lojas = Array.isArray(refs?.lojas) ? refs.lojas : [];
    lojas.forEach((l) => map.set(l.nome, l));
    return map;
  }, [refs]);

  // Cards por loja no formato novo
  const cardsPorLoja = useMemo(() => {
    const mapEnv = new Map(enviosPorLoja.map((x) => [x.loja, x]));
    const mapDev = new Map(devolPorLoja.map((x) => [x.loja, x]));

    // quebra por fábrica
    const mapBreak = new Map(); // loja -> { '10':{qtde,valor}, '20':..., '30':... }
    enviosLojaFabrica.forEach((r) => {
      if (!FABRICAS.includes(r.empresa)) return;
      if (!mapBreak.has(r.loja)) {
        mapBreak.set(r.loja, {
          '10': { qtde: 0, valor: 0 },
          '20': { qtde: 0, valor: 0 },
          '30': { qtde: 0, valor: 0 },
        });
      }
      const b = mapBreak.get(r.loja);
      b[r.empresa].qtde += r.qtde;
      b[r.empresa].valor += r.valor;
    });

    const lojasSet = new Set([
      ...mapEnv.keys(),
      ...mapDev.keys(),
      ...mapBreak.keys(),
    ]);

    const list = [];
    lojasSet.forEach((loja) => {
      const env = mapEnv.get(loja) || { qtde: 0, valor: 0 };
      const dev = mapDev.get(loja) || { qtde: 0, valor: 0 };
      const brk = mapBreak.get(loja) || {
        '10': { qtde: 0, valor: 0 },
        '20': { qtde: 0, valor: 0 },
        '30': { qtde: 0, valor: 0 },
      };

      const pct = enviosTotais.valor > 0 ? (env.valor / enviosTotais.valor) * 100 : 0;

      const meta = lojaMetaMap.get(loja);
      const lojaLabel = meta
        ? `${meta.empresa} · ${meta.nome}`
        : loja;

      list.push({
        loja,
        lojaLabel,
        pctEnvio: pct,
        enviado: env,
        devolvido: dev,
        porFabrica: brk,
      });
    });

    // sem “Ordenar” no UI, mas precisamos de uma ordem consistente:
    // maior valor enviado primeiro
    return list.sort((a, b) => b.enviado.valor - a.enviado.valor);
  }, [enviosPorLoja, devolPorLoja, enviosLojaFabrica, enviosTotais.valor, lojaMetaMap]);

  const onChange = (key) => (e) => {
    const val = e.target.value;
    setFiltros((prev) => ({
      ...prev,
      [key]: key === 'tabela' ? Number(val) : val,
    }));
  };

  const toggleGrupo = (g) => {
    setGruposSelecionados((prev) => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };

  const clearGrupos = () => setGruposSelecionados(new Set());
  const markAllGrupos = () => setGruposSelecionados(new Set(grupos));

  const onAplicar = () => fetchDados();

  const gruposResumo = useMemo(() => {
    const n = gruposSelecionados.size;
    if (n === 0) return 'Todos';
    if (n === 1) return Array.from(gruposSelecionados)[0];
    return `${n} selecionados`;
  }, [gruposSelecionados]);

  return (
    <div className={styles.page}>
      <Header />

      <Link redirectTo="/home" />

      <div className={styles.container}>
        <PageTitle src="/images/common/caminhao.svg" title="Distribuições" />

        {/* Filtros */}
        <div className={styles.filtersCard}>
          <div className={styles.filtersGrid}>
            <div className={styles.field}>
              <label>Data inicial</label>
              <input type="date" value={filtros.dataIni} onChange={onChange('dataIni')} />
            </div>

            <div className={styles.field}>
              <label>Data final</label>
              <input type="date" value={filtros.dataFim} onChange={onChange('dataFim')} />
            </div>

            <div className={styles.field}>
              <label>Código do produto</label>
              <input
                type="text"
                placeholder="Ex.: 54321"
                value={filtros.codItem}
                onChange={onChange('codItem')}
              />
            </div>

            {/* Multi-select de grupos */}
            <div className={styles.field} ref={dropdownRef}>
              <label>Grupo</label>

              <button
                type="button"
                className={styles.multiSelectBtn}
                onClick={() => setDropdownOpen((v) => !v)}
                disabled={grupos.length === 0}
                title="Abrir seleção de grupos"
              >
                <span className={styles.multiSelectText}>{gruposResumo}</span>
                <span className={styles.multiSelectChevron}>{dropdownOpen ? '▲' : '▼'}</span>
              </button>

              {dropdownOpen ? (
                <div className={styles.multiSelectPanel}>
                  <div className={styles.multiSelectActions}>
                    <button type="button" className={styles.linkBtn} onClick={clearGrupos}>
                      Limpar
                    </button>
                    <button
                      type="button"
                      className={styles.linkBtn}
                      onClick={markAllGrupos}
                    >
                      Marcar todos
                    </button>
                  </div>

                  <div className={styles.checkList}>
                    {grupos.map((g) => {
                      const checked = gruposSelecionados.has(g);
                      return (
                        <label key={g} className={styles.checkItem}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleGrupo(g)}
                          />
                          <span>{g}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ) : null}
            </div>

            <div className={styles.field}>
              <label>Tabela de preço</label>
              <select value={filtros.tabela} onChange={onChange('tabela')}>
                {Object.entries(TABELAS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.actions}>
              <button className={styles.primaryBtn} onClick={onAplicar} disabled={isLoading}>
                Aplicar filtros
              </button>
            </div>
          </div>

          {erro ? <div className={styles.errorBox}>{erro}</div> : null}
        </div>

        {isLoading ? <LoadingScreen /> : null}

        {!isLoading && dados ? (
          <>
            {/* Cards por loja: 2 colunas desktop, 1 no mobile */}
            <div className={styles.cardsGrid}>
              {cardsPorLoja.map((c) => (
                <div key={c.loja} className={styles.lojaCard}>
                  {/* Cabeçalho */}
                  <div className={styles.cardHeader}>
                    <div className={styles.lojaTitle}>{c.lojaLabel}</div>
                    <div className={styles.lojaPct} title="% do valor total enviado no período">
                      {formatNum(c.pctEnvio)}%
                    </div>
                  </div>

                  {/* Total Enviado / Total Devolvido / Saldo */}
                  <div className={styles.middleRow}>
                    <div className={styles.kpiBox}>
                      <div className={styles.kpiLabel} style={{ color: "#2E8B57" }}>
                        TOTAL ENVIADO
                      </div>
                      <div className={styles.kpiMain}>{formatNum(c.enviado.qtde)} un</div>
                      <div className={styles.kpiSub}>{formatBRL(c.enviado.valor)}</div>
                    </div>

                    <div className={styles.kpiBox}>
                      <div className={styles.kpiLabel} style={{ color: "#B22222" }}>
                        TOTAL DEVOLVIDO
                      </div>
                      <div className={styles.kpiMain}>{formatNum(c.devolvido.qtde)} un</div>
                      <div className={styles.kpiSub}>{formatBRL(c.devolvido.valor)}</div>
                    </div>

                    {/* 🧮 Saldo */}
                    <div className={styles.kpiBox}>
                      <div
                        className={styles.kpiLabel}
                        style={{
                          color:
                            (c.enviado.qtde - c.devolvido.qtde) >= 0 ? "#2E8B57" : "#B22222",
                        }}
                      >
                        SALDO
                      </div>
                      <div className={styles.kpiMain}>
                        {formatNum(c.enviado.qtde - c.devolvido.qtde)} un
                      </div>
                      <div className={styles.kpiSub}>
                        {formatBRL(c.enviado.valor - c.devolvido.valor)}
                      </div>
                    </div>
                  </div>

                  {/* Footer: grid 10/20/30 */}
                  <div className={styles.footerTitle}>Enviado por...</div>

                  <div className={styles.footerGrid}>
                    {FABRICAS.map((f) => (
                      <div key={f} className={styles.footerItem}>
                        <div className={styles.footerLabel}>Empresa {f}</div>
                        <div className={styles.footerMain}>
                          {formatNum(c.porFabrica[f]?.qtde || 0)} un
                        </div>
                        <div className={styles.footerSub}>
                          {formatBRL(c.porFabrica[f]?.valor || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default Distribuicoes;
